import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Avatar,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    Fade,
    useTheme,
    useMediaQuery,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Checkbox,
    Pagination,
    Chip,
} from '@mui/material';
import {
    SportsEsports as SportsEsportsIcon,
    Add as AddIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    PlayArrow as PlayArrowIcon,
    Stop as StopIcon,
    Edit as EditIcon,
    FilterList as FilterListIcon,
    ExpandMore as ExpandMoreIcon,
    Comment as CommentIcon,
    LocalFireDepartment as LocalFireDepartmentIcon,
    SyncAlt as SyncAltIcon,
    OpenInNew as OpenInNewIcon,
    AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { getTrackerDashboardData, updateMatch } from '../../services/tracker-data';
import { getAllInkColors } from './InkColorIcon';

function MatchesList({ deleteMode, selectedMatches, onToggleDeleteMode, onSelectMatch, onDeleteSelectedMatches, onStatsUpdate, onMatchAdded, onOpenQuickMatch, onEditMatch, refreshTrigger, trackerFilters }) {
    const { currentUser } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const [matches, setMatches] = useState([]);
    const [allArchetypes, setAllArchetypes] = useState([]);
    const [allDecks, setAllDecks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [resultFilter, setResultFilter] = useState('');
    const [archetypeFilter, setArchetypeFilter] = useState('');
    const [playDrawFilter, setPlayDrawFilter] = useState('');
    const [deckFilter, setDeckFilter] = useState('');
    const [tryhardOnlyFilter, setTryhardOnlyFilter] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const matchesPerPage = 10;
    const [expandedComments, setExpandedComments] = useState(new Set());
    const [archetypeAssigning, setArchetypeAssigning] = useState({});

    const inkColors = getAllInkColors();

    const loadMatches = useCallback(async () => {
        if (!currentUser) return;
        try {
            setLoading(true);
            setError(null);
            const dashboardData = await getTrackerDashboardData(currentUser.uid, trackerFilters);
            setMatches(dashboardData.matches);
            setAllArchetypes(dashboardData.archetypes);
            setAllDecks(dashboardData.allDecks);
        } catch (err) {
            console.error('Błąd podczas ładowania meczy:', err);
            setError('Nie udało się załadować meczy. Spróbuj ponownie.');
        } finally {
            setLoading(false);
        }
    }, [currentUser, trackerFilters]);

    useEffect(() => {
        loadMatches();
    }, [loadMatches, refreshTrigger]);

    const handleAssignArchetype = useCallback(async (matchId, archetypeId) => {
        setArchetypeAssigning(prev => ({ ...prev, [matchId]: true }));
        try {
            await updateMatch(matchId, { opponentArchetypeId: archetypeId });
            const archetypeInfo = allArchetypes.find(a => a.id === archetypeId) || null;
            setMatches(prev => prev.map(m =>
                m.id === matchId
                    ? { ...m, opponentArchetypeId: archetypeId, opponentArchetypeInfo: archetypeInfo }
                    : m
            ));
            if (onStatsUpdate) onStatsUpdate();
        } catch (err) {
            console.error('Failed to assign archetype', err);
        } finally {
            setArchetypeAssigning(prev => ({ ...prev, [matchId]: false }));
        }
    }, [allArchetypes, onStatsUpdate]);

    const getInkColor = (colorName) => {
        const lower = (colorName || '').toLowerCase();
        const color = inkColors.find(c => c.value.toLowerCase() === lower);
        return color ? color.color : '#6b7280';
    };

    const toValidDate = (value) => {
        if (value === null || value === undefined) return null;

        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }

        if (typeof value === 'number' || typeof value === 'string') {
            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }

        if (typeof value === 'object' && typeof value.seconds === 'number') {
            const parsed = new Date(value.seconds * 1000);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        }

        return null;
    };

    const getMatchDate = (match) =>
        toValidDate(match?.timestamp) ||
        toValidDate(match?.started_at) ||
        toValidDate(match?.createdAt) ||
        toValidDate(match?.modifiedAt);

    const formatDate = (match) => {
        const date = getMatchDate(match);
        if (!date) return 'Brak daty';

        return date.toLocaleDateString('pl-PL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatShortDate = (match) => {
        const date = getMatchDate(match);
        if (!date) return '--.--';

        return date.toLocaleDateString('pl-PL', {
            day: '2-digit',
            month: '2-digit'
        });
    };

    const getResultIcon = (result) => {
        switch (result) {
            case 'win':
                return <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />;
            case 'lose':
                return <CancelIcon sx={{ color: '#ef4444', fontSize: 20 }} />;
            default:
                return <SportsEsportsIcon sx={{ color: '#6b7280', fontSize: 20 }} />;
        }
    };

    const getResultColor = (result) => {
        switch (result) {
            case 'win':
                return '#10b981';
            case 'lose':
                return '#ef4444';
            default:
                return '#6b7280';
        }
    };

    const getPlayDrawIcon = (playDraw) => {
        switch (playDraw) {
            case 'play':
                return <PlayArrowIcon sx={{ fontSize: 20 }} />;
            case 'draw':
                return <StopIcon sx={{ fontSize: 20 }} />;
            default:
                return null;
        }
    };

    const matchesWithDate = useMemo(() => matches.map((match) => ({
        ...match,
        __matchDateMs: getMatchDate(match)?.getTime() ?? 0
    })), [matches]);

    // Filtrowanie i sortowanie meczy
    const filteredMatches = useMemo(() => matchesWithDate.filter(match => {
        if (resultFilter && match.result !== resultFilter) {
            return false;
        }
        if (archetypeFilter) {
            if (archetypeFilter === 'null' && match.opponentArchetypeId !== null) {
                return false;
            } else if (archetypeFilter !== 'null' && match.opponentArchetypeId !== archetypeFilter) {
                return false;
            }
        }
        if (playDrawFilter && match.playDraw !== playDrawFilter) {
            return false;
        }
        if (deckFilter && match.deckId !== deckFilter) {
            return false;
        }
        if (tryhardOnlyFilter && !Boolean(match.tryhard)) {
            return false;
        }
        return true;
    }).sort((a, b) => b.__matchDateMs - a.__matchDateMs), [matchesWithDate, resultFilter, archetypeFilter, playDrawFilter, deckFilter, tryhardOnlyFilter]);

    // Paginacja
    const totalPages = Math.ceil(filteredMatches.length / matchesPerPage);
    const startIndex = (currentPage - 1) * matchesPerPage;
    const endIndex = startIndex + matchesPerPage;
    const paginatedMatches = filteredMatches.slice(startIndex, endIndex);

    // Resetuj stronę gdy zmieniają się filtry
    useEffect(() => {
        setCurrentPage(1);
    }, [resultFilter, archetypeFilter, playDrawFilter, deckFilter, tryhardOnlyFilter]);

    const toggleCommentExpansion = (matchId) => {
        setExpandedComments(prev => {
            const newSet = new Set(prev);
            if (newSet.has(matchId)) {
                newSet.delete(matchId);
            } else {
                newSet.add(matchId);
            }
            return newSet;
        });
    };


    if (!currentUser) {
        return (
            <Alert severity="warning">
                Musisz być zalogowany, aby zobaczyć swoje mecze.
            </Alert>
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#7c3aed' }} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" onClose={() => setError(null)}>
                {error}
            </Alert>
        );
    }

    if (matches.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
                <SportsEsportsIcon sx={{ fontSize: 64, color: 'rgba(196, 181, 253, 0.3)', mb: 2 }} />
                <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                    Brak zarejestrowanych meczów
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', mb: 3 }}>
                    Dodaj pierwszy mecz, aby uruchomić statystyki i analizę matchupów.
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => onOpenQuickMatch && onOpenQuickMatch()}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    Dodaj pierwszy mecz
                </Button>
            </Box>
        );
    }

    // Sprawdź czy są aktywne filtry
    const hasActiveFilters = resultFilter || archetypeFilter || playDrawFilter || deckFilter || tryhardOnlyFilter;

    const renderTryhardOnlyButton = () => (
        <Tooltip title="Pokaż tylko mecze oznaczone jako tryhard">
            <Button
                size="small"
                variant={tryhardOnlyFilter ? 'contained' : 'outlined'}
                onClick={() => setTryhardOnlyFilter((prev) => !prev)}
                startIcon={<LocalFireDepartmentIcon sx={{ fontSize: 16 }} />}
                sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    color: tryhardOnlyFilter ? 'white' : 'rgba(251, 146, 60, 0.95)',
                    borderColor: 'rgba(249, 115, 22, 0.45)',
                    background: tryhardOnlyFilter ? 'linear-gradient(135deg, #f97316, #ea580c)' : 'transparent',
                    '&:hover': {
                        borderColor: 'rgba(249, 115, 22, 0.7)',
                        backgroundColor: tryhardOnlyFilter ? undefined : 'rgba(249, 115, 22, 0.12)'
                    }
                }}
            >
                Tylko tryhard
            </Button>
        </Tooltip>
    );

    const renderFilters = () => {
        if (isSmallMobile) {
            // Mobile: Accordion z filtrami
            return (
                <Accordion
                    defaultExpanded={hasActiveFilters}
                    sx={{
                        backgroundColor: 'rgba(17, 24, 39, 0.4)',
                        border: '1px solid rgba(76, 29, 149, 0.2)',
                        borderRadius: '12px !important',
                        mb: 3,
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                            margin: '0 0 24px 0'
                        }
                    }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(196, 181, 253, 0.7)' }} />}
                        sx={{
                            '& .MuiAccordionSummary-content': {
                                alignItems: 'center',
                                gap: 1
                            }
                        }}
                    >
                        <FilterListIcon sx={{ color: 'rgba(196, 181, 253, 0.7)' }} />
                        <Typography sx={{ color: 'white', fontWeight: 600 }}>
                            Filtry {hasActiveFilters && `(${[
                                resultFilter && 'Wynik',
                                archetypeFilter && 'Archetyp',
                                playDrawFilter && 'Play/Draw',
                                deckFilter && 'Talii',
                                tryhardOnlyFilter && 'Tryhard'
                            ].filter(Boolean).length})`}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {renderTryhardOnlyButton()}
                            {/* Filtry w dwóch kolumnach */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControl size="small" sx={{ flex: 1 }}>
                                    <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                        Wynik
                                    </InputLabel>
                                    <Select
                                        value={resultFilter}
                                        onChange={(e) => setResultFilter(e.target.value)}
                                        label="Wynik"
                                        sx={{
                                            color: 'white',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(76, 29, 149, 0.5)'
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(76, 29, 149, 0.8)'
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#7c3aed'
                                            }
                                        }}
                                    >
                                        <MenuItem value="">
                                            <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                                Wszystkie
                                            </Typography>
                                        </MenuItem>
                                        <MenuItem value="win">Wygrana</MenuItem>
                                        <MenuItem value="lose">Przegrana</MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ flex: 1 }}>
                                    <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                        Play/Draw
                                    </InputLabel>
                                    <Select
                                        value={playDrawFilter}
                                        onChange={(e) => setPlayDrawFilter(e.target.value)}
                                        label="Play/Draw"
                                        sx={{
                                            color: 'white',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(76, 29, 149, 0.5)'
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: 'rgba(76, 29, 149, 0.8)'
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#7c3aed'
                                            }
                                        }}
                                    >
                                        <MenuItem value="">
                                            <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                                Wszystkie
                                            </Typography>
                                        </MenuItem>
                                        <MenuItem value="play">Play</MenuItem>
                                        <MenuItem value="draw">Draw</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Archetyp - pełna szerokość */}
                            <FormControl size="small" sx={{ width: '100%' }}>
                                <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                    Archetyp przeciwnika
                                </InputLabel>
                                <Select
                                    value={archetypeFilter}
                                    onChange={(e) => setArchetypeFilter(e.target.value)}
                                    label="Archetyp przeciwnika"
                                    sx={{
                                        color: 'white',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.5)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.8)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#7c3aed'
                                        }
                                    }}
                                >
                                    <MenuItem value="">
                                        <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                            Wszystkie archetypy
                                        </Typography>
                                    </MenuItem>
                                    <MenuItem value="null">
                                        <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                            Brak archetypu
                                        </Typography>
                                    </MenuItem>
                                    {allArchetypes.map(archetype => (
                                        <MenuItem key={archetype.id} value={archetype.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                <Typography sx={{ color: 'white', flex: 1, fontSize: '0.875rem' }}>
                                                    {archetype.name}
                                                </Typography>
                                                {archetype.ink_colors && (
                                                    <Box sx={{ display: 'flex', gap: 0.3 }}>
                                                        {archetype.ink_colors.map((color, index) => {
                                                            const colorData = inkColors.find(c => c.value.toLowerCase() === (color || '').toLowerCase());
                                                            return (
                                                                <Avatar
                                                                    key={index}
                                                                    src={colorData?.icon}
                                                                    sx={{
                                                                        width: 14,
                                                                        height: 14,
                                                                        backgroundColor: getInkColor(color),
                                                                        border: `1px solid ${getInkColor(color)}`
                                                                    }}
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Filtr talii - pełna szerokość */}
                            <FormControl size="small" sx={{ width: '100%' }}>
                                <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                    Moja talii
                                </InputLabel>
                                <Select
                                    value={deckFilter}
                                    onChange={(e) => setDeckFilter(e.target.value)}
                                    label="Moja talii"
                                    sx={{
                                        color: 'white',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.5)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(76, 29, 149, 0.8)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#7c3aed'
                                        }
                                    }}
                                >
                                    <MenuItem value="">
                                        <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                            Wszystkie talii
                                        </Typography>
                                    </MenuItem>
                                    {allDecks.map((deck) => (
                                        <MenuItem key={deck.id} value={deck.id}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                <Typography sx={{ color: 'white', flex: 1, fontSize: '0.875rem' }}>
                                                    {deck.name}
                                                </Typography>
                                                {deck.archetype && (() => {
                                                    const archetype = allArchetypes.find(arch => arch.id === deck.archetype);
                                                    return archetype ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)', fontSize: '0.7rem' }}>
                                                                {archetype.name}
                                                            </Typography>
                                                            {archetype.ink_colors && (
                                                                <Box sx={{ display: 'flex', gap: 0.2 }}>
                                                                    {archetype.ink_colors.slice(0, 2).map((color, colorIndex) => {
                                                                        const colorData = inkColors.find(c => c.value.toLowerCase() === (color || '').toLowerCase());
                                                                        return (
                                                                            <Avatar
                                                                                key={colorIndex}
                                                                                src={colorData?.icon}
                                                                                sx={{
                                                                                    width: 12,
                                                                                    height: 12,
                                                                                    backgroundColor: getInkColor(color),
                                                                                    border: `1px solid ${getInkColor(color)}`
                                                                                }}
                                                                            />
                                                                        );
                                                                    })}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    ) : null;
                                                })()}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            );
        }

        if (isMobile) {
            // Tablet: Filtry w dwóch rzędach
            return (
                <>
                    <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                            Wynik
                        </InputLabel>
                        <Select
                            value={resultFilter}
                            onChange={(e) => setResultFilter(e.target.value)}
                            label="Wynik"
                            sx={{
                                color: 'white',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(76, 29, 149, 0.5)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(76, 29, 149, 0.8)'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#7c3aed'
                                }
                            }}
                        >
                            <MenuItem value="">
                                <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                    Wszystkie wyniki
                                </Typography>
                            </MenuItem>
                            <MenuItem value="win">Wygrana</MenuItem>
                            <MenuItem value="lose">Przegrana</MenuItem>
                        </Select>
                    </FormControl>

                        <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                            Archetyp
                        </InputLabel>
                        <Select
                            value={archetypeFilter}
                            onChange={(e) => setArchetypeFilter(e.target.value)}
                            label="Archetyp"
                            sx={{
                                color: 'white',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(76, 29, 149, 0.5)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(76, 29, 149, 0.8)'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#7c3aed'
                                }
                            }}
                        >
                            <MenuItem value="">
                                <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                    Wszystkie archetypy
                                </Typography>
                            </MenuItem>
                            {allArchetypes.map(archetype => (
                                <MenuItem key={archetype.id} value={archetype.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        <Typography sx={{ color: 'white', flex: 1, fontSize: '0.875rem' }}>
                                            {archetype.name}
                                        </Typography>
                                        {archetype.ink_colors && (
                                            <Box sx={{ display: 'flex', gap: 0.3 }}>
                                                {archetype.ink_colors.map((color, index) => {
                                                    const colorData = inkColors.find(c => c.value.toLowerCase() === (color || '').toLowerCase());
                                                    return (
                                                        <Avatar
                                                            key={index}
                                                            src={colorData?.icon}
                                                            sx={{
                                                                width: 14,
                                                                height: 14,
                                                                backgroundColor: getInkColor(color),
                                                                border: `1px solid ${getInkColor(color)}`
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                        <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                            Play/Draw
                        </InputLabel>
                        <Select
                            value={playDrawFilter}
                            onChange={(e) => setPlayDrawFilter(e.target.value)}
                            label="Play/Draw"
                            sx={{
                                color: 'white',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(76, 29, 149, 0.5)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(76, 29, 149, 0.8)'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#7c3aed'
                                }
                            }}
                        >
                            <MenuItem value="">
                                <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                    Wszystkie
                                </Typography>
                            </MenuItem>
                            <MenuItem value="play">Play</MenuItem>
                            <MenuItem value="draw">Draw</MenuItem>
                        </Select>
                    </FormControl>

                        <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                            Moja talii
                        </InputLabel>
                        <Select
                            value={deckFilter}
                            onChange={(e) => setDeckFilter(e.target.value)}
                            label="Moja talii"
                            sx={{
                                color: 'white',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(76, 29, 149, 0.5)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(76, 29, 149, 0.8)'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#7c3aed'
                                }
                            }}
                        >
                            <MenuItem value="">
                                <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                    Wszystkie talii
                                </Typography>
                            </MenuItem>
                            {allDecks.map((deck) => (
                                <MenuItem key={deck.id} value={deck.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        <Typography sx={{ color: 'white', flex: 1, fontSize: '0.875rem' }}>
                                            {deck.name}
                                        </Typography>
                                        {deck.archetype && (() => {
                                            const archetype = allArchetypes.find(arch => arch.id === deck.archetype);
                                            return archetype ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)', fontSize: '0.7rem' }}>
                                                        {archetype.name}
                                                    </Typography>
                                                    {archetype.ink_colors && (
                                                        <Box sx={{ display: 'flex', gap: 0.2 }}>
                                                            {archetype.ink_colors.slice(0, 2).map((color, colorIndex) => {
                                                                const colorData = inkColors.find(c => c.value.toLowerCase() === (color || '').toLowerCase());
                                                                return (
                                                                    <Avatar
                                                                        key={colorIndex}
                                                                        src={colorData?.icon}
                                                                        sx={{
                                                                            width: 12,
                                                                            height: 12,
                                                                            backgroundColor: getInkColor(color),
                                                                            border: `1px solid ${getInkColor(color)}`
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                </Box>
                                            ) : null;
                                        })()}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                        </FormControl>
                    </Box>
                    {renderTryhardOnlyButton()}
                </>
            );
        }

        // Desktop: Oryginalny layout
        return (
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                        Wynik
                    </InputLabel>
                    <Select
                        value={resultFilter}
                        onChange={(e) => setResultFilter(e.target.value)}
                        label="Wynik"
                        sx={{
                            color: 'white',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(76, 29, 149, 0.5)'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(76, 29, 149, 0.8)'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#7c3aed'
                            }
                        }}
                    >
                        <MenuItem value="">
                            <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                Wszystkie wyniki
                            </Typography>
                        </MenuItem>
                        <MenuItem value="win">Wygrana</MenuItem>
                        <MenuItem value="lose">Przegrana</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                        Archetyp przeciwnika
                    </InputLabel>
                    <Select
                        value={archetypeFilter}
                        onChange={(e) => setArchetypeFilter(e.target.value)}
                        label="Archetyp przeciwnika"
                        sx={{
                            color: 'white',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(76, 29, 149, 0.5)'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(76, 29, 149, 0.8)'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#7c3aed'
                            }
                        }}
                    >
                        <MenuItem value="">
                            <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                Wszystkie archetypy
                            </Typography>
                        </MenuItem>
                        <MenuItem value="null">
                            <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                Brak archetypu
                            </Typography>
                        </MenuItem>
                        {allArchetypes.map(archetype => (
                            <MenuItem key={archetype.id} value={archetype.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    <Typography sx={{ color: 'white', flex: 1 }}>
                                        {archetype.name}
                                    </Typography>
                                    {archetype.ink_colors && (
                                        <Box sx={{ display: 'flex', gap: 0.3 }}>
                                            {archetype.ink_colors.map((color, index) => {
                                                const colorData = inkColors.find(c => c.value.toLowerCase() === (color || '').toLowerCase());
                                                return (
                                                    <Avatar
                                                        key={index}
                                                        src={colorData?.icon}
                                                        sx={{
                                                            width: 16,
                                                            height: 16,
                                                            backgroundColor: getInkColor(color),
                                                            border: `1px solid ${getInkColor(color)}`
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    )}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                        Play/Draw
                    </InputLabel>
                    <Select
                        value={playDrawFilter}
                        onChange={(e) => setPlayDrawFilter(e.target.value)}
                        label="Play/Draw"
                        sx={{
                            color: 'white',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(76, 29, 149, 0.5)'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(76, 29, 149, 0.8)'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#7c3aed'
                            }
                        }}
                    >
                        <MenuItem value="">
                            <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                Wszystkie
                            </Typography>
                        </MenuItem>
                        <MenuItem value="play">Play</MenuItem>
                        <MenuItem value="draw">Draw</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                        Moja talii
                    </InputLabel>
                    <Select
                        value={deckFilter}
                        onChange={(e) => setDeckFilter(e.target.value)}
                        label="Moja talii"
                        sx={{
                            color: 'white',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(76, 29, 149, 0.5)'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(76, 29, 149, 0.8)'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#7c3aed'
                            }
                        }}
                    >
                        <MenuItem value="">
                            <Typography sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>
                                Wszystkie talii
                            </Typography>
                        </MenuItem>
                        {allDecks.map((deck) => (
                            <MenuItem key={deck.id} value={deck.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    <Typography sx={{ color: 'white', flex: 1 }}>
                                        {deck.name}
                                    </Typography>
                                    {deck.archetype && (() => {
                                        const archetype = allArchetypes.find(arch => arch.id === deck.archetype);
                                        return archetype ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)', fontSize: '0.8rem' }}>
                                                    {archetype.name}
                                                </Typography>
                                                {archetype.ink_colors && (
                                                    <Box sx={{ display: 'flex', gap: 0.2 }}>
                                                        {archetype.ink_colors.slice(0, 2).map((color, colorIndex) => {
                                                            const colorData = inkColors.find(c => c.value.toLowerCase() === (color || '').toLowerCase());
                                                            return (
                                                                <Avatar
                                                                    key={colorIndex}
                                                                    src={colorData?.icon}
                                                                    sx={{
                                                                        width: 14,
                                                                        height: 14,
                                                                        backgroundColor: getInkColor(color),
                                                                        border: `1px solid ${getInkColor(color)}`
                                                                    }}
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                            </Box>
                                        ) : null;
                                    })()}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {renderTryhardOnlyButton()}
            </Box>
        );
    };

    return (
        <Box>
            {/* Filtry */}
            {renderFilters()}

            {/* Lista meczy */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {paginatedMatches.map((match) => (
                    <Fade in={true} timeout={300} key={match.id}>
                        <Box>
                            {isSmallMobile ? (
                                // Mobile: Kompaktowy layout
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 1.5,
                                    backgroundColor: deleteMode && selectedMatches.has(match.id)
                                        ? 'rgba(239, 68, 68, 0.2)'
                                        : 'rgba(17, 24, 39, 0.4)',
                                    border: deleteMode && selectedMatches.has(match.id)
                                        ? '1px solid rgba(239, 68, 68, 0.5)'
                                        : '1px solid rgba(76, 29, 149, 0.2)',
                                    borderRadius: '8px',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: deleteMode && selectedMatches.has(match.id)
                                            ? 'rgba(239, 68, 68, 0.3)'
                                            : 'rgba(17, 24, 39, 0.6)',
                                        border: deleteMode && selectedMatches.has(match.id)
                                            ? '1px solid rgba(239, 68, 68, 0.7)'
                                            : '1px solid rgba(76, 29, 149, 0.4)'
                                    }
                                }}>
                                    {/* Checkbox do zaznaczania (tylko w trybie usuwania) */}
                                    {deleteMode && (
                                        <Checkbox
                                            checked={selectedMatches.has(match.id)}
                                            onChange={() => onSelectMatch(match.id)}
                                            sx={{
                                                color: 'rgba(239, 68, 68, 0.7)',
                                                '&.Mui-checked': {
                                                    color: '#ef4444'
                                                }
                                            }}
                                        />
                                    )}

                                    {/* Lewa strona - wynik i podstawowe info */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                                        {/* Wynik z ikoną */}
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            backgroundColor: match.result === 'win' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                            border: `2px solid ${getResultColor(match.result)}`
                                        }}>
                                            {getResultIcon(match.result)}
                                        </Box>

                                        {/* Informacje o meczu - kompaktowo */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            {/* Moja talia - ikony kolorów + nazwa */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {(() => {
                                                    const myArch = match.myDeckInfo?.archetype
                                                        ? allArchetypes.find(a => a.id === match.myDeckInfo.archetype)
                                                        : null;
                                                    const myColors = myArch?.ink_colors || [];
                                                    return myColors.length > 0 ? (
                                                        <Box sx={{ display: 'flex', gap: 0.15, flexShrink: 0 }}>
                                                            {myColors.slice(0, 2).map((color, i) => {
                                                                const colorData = inkColors.find(c => c.value.toLowerCase() === (color || '').toLowerCase());
                                                                return <Avatar key={i} src={colorData?.icon} sx={{ width: 11, height: 11, backgroundColor: getInkColor(color), border: `1px solid ${getInkColor(color)}` }} />;
                                                            })}
                                                        </Box>
                                                    ) : null;
                                                })()}
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'white',
                                                        fontWeight: 600,
                                                        fontSize: '0.8rem',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        flex: 1
                                                    }}
                                                >
                                                    {match.myDeckInfo?.name || 'Nieznana'}
                                                </Typography>
                                            </Box>

                                            {/* vs z archetypami */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                {/* Mój archetyp - ikony kolorów + nazwa */}
                                                {match.myDeckInfo?.archetype && (() => {
                                                    const myArchetype = allArchetypes.find(arch => arch.id === match.myDeckInfo.archetype);
                                                    return myArchetype ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                                            {myArchetype.ink_colors && (
                                                                <Box sx={{ display: 'flex', gap: 0.2 }}>
                                                                    {myArchetype.ink_colors.slice(0, 2).map((color, colorIndex) => {
                                                                        const colorData = inkColors.find(c => c.value.toLowerCase() === (color || '').toLowerCase());
                                                                        return (
                                                                            <Avatar
                                                                                key={colorIndex}
                                                                                src={colorData?.icon}
                                                                                sx={{
                                                                                    width: 10,
                                                                                    height: 10,
                                                                                    backgroundColor: getInkColor(color),
                                                                                    border: `1px solid ${getInkColor(color)}`
                                                                                }}
                                                                            />
                                                                        );
                                                                    })}
                                                                    {myArchetype.ink_colors.length > 2 && (
                                                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.5)', fontSize: '0.5rem' }}>
                                                                            +{myArchetype.ink_colors.length - 2}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            )}
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: 'rgba(196, 181, 253, 0.8)',
                                                                    fontSize: '0.7rem',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                    maxWidth: '60px'
                                                                }}
                                                            >
                                                                {myArchetype.name}
                                                            </Typography>
                                                        </Box>
                                                    ) : null;
                                                })()}

                                                {/* vs */}
                                                <Typography variant="caption" sx={{
                                                    color: 'rgba(196, 181, 253, 0.4)',
                                                    fontSize: '0.6rem',
                                                    mx: 0.5
                                                }}>
                                                    vs
                                                </Typography>

                                                {/* Przeciwnik - ikony kolorów + nazwa */}
                                                {(() => {
                                                    const displayColors = match.opponentArchetypeInfo?.ink_colors || match.opponentColors || [];
                                                    const oColors = (match.opponentColors || []).map(c => c.toLowerCase());
                                                    const matchingArchetypes = !match.opponentArchetypeId && oColors.length > 0
                                                        ? allArchetypes.filter(a => {
                                                            const aColors = (a.ink_colors || []).map(c => c.toLowerCase());
                                                            return aColors.length === oColors.length && oColors.every(c => aColors.includes(c));
                                                          })
                                                        : [];
                                                    return (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                                            {displayColors.length > 0 && (
                                                                <Box sx={{ display: 'flex', gap: 0.2 }}>
                                                                    {displayColors.slice(0, 2).map((color, colorIndex) => {
                                                                        const colorData = inkColors.find(c => c.value.toLowerCase() === (color || '').toLowerCase());
                                                                        return (
                                                                            <Avatar
                                                                                key={colorIndex}
                                                                                src={colorData?.icon}
                                                                                sx={{
                                                                                    width: 10,
                                                                                    height: 10,
                                                                                    backgroundColor: getInkColor(color),
                                                                                    border: `1px solid ${getInkColor(color)}`
                                                                                }}
                                                                            />
                                                                        );
                                                                    })}
                                                                    {displayColors.length > 2 && (
                                                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.5)', fontSize: '0.5rem' }}>
                                                                            +{displayColors.length - 2}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            )}
                                                            {match.opponentArchetypeId ? (
                                                                <Typography
                                                                    variant="caption"
                                                                    onClick={() => navigate(`/archetype/${match.opponentArchetypeId}`)}
                                                                    sx={{
                                                                        color: 'rgba(196, 181, 253, 0.8)',
                                                                        fontSize: '0.7rem',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                        flex: 1,
                                                                        cursor: 'pointer',
                                                                        '&:hover': { color: '#c4b5fd', textDecoration: 'underline' }
                                                                    }}
                                                                >
                                                                    {match.opponentArchetypeInfo?.name}
                                                                </Typography>
                                                            ) : matchingArchetypes.length > 0 ? (
                                                                <Select
                                                                    size="small"
                                                                    displayEmpty
                                                                    value=""
                                                                    disabled={!!archetypeAssigning[match.id]}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={(e) => { e.stopPropagation(); handleAssignArchetype(match.id, e.target.value); }}
                                                                    sx={{
                                                                        fontSize: '0.6rem',
                                                                        height: 20,
                                                                        color: 'rgba(196, 181, 253, 0.6)',
                                                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(124, 58, 237, 0.3)' },
                                                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(124, 58, 237, 0.6)' },
                                                                        '& .MuiSelect-select': { py: 0, px: 0.5 },
                                                                    }}
                                                                    renderValue={() => archetypeAssigning[match.id] ? '…' : 'Przypisz'}
                                                                >
                                                                    {matchingArchetypes.map(a => (
                                                                        <MenuItem key={a.id} value={a.id}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                {(a.ink_colors || []).slice(0, 2).map((color, i) => {
                                                                                    const colorData = inkColors.find(c => c.value.toLowerCase() === (color || '').toLowerCase());
                                                                                    return <Avatar key={i} src={colorData?.icon} sx={{ width: 12, height: 12, backgroundColor: getInkColor(color), border: `1px solid ${getInkColor(color)}` }} />;
                                                                                })}
                                                                                <Typography variant="caption" sx={{ color: 'white' }}>{a.name}</Typography>
                                                                            </Box>
                                                                        </MenuItem>
                                                                    ))}
                                                                </Select>
                                                            ) : (
                                                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.8)', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                                                    {displayColors.length > 0 ? '' : '—'}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    );
                                                })()}
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Prawa strona - Play/Draw, data i komentarz */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mx: 1 }}>
                                        {/* Play/Draw - tylko ikona */}
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 24,
                                            height: 24,
                                            borderRadius: '4px',
                                            backgroundColor: match.playDraw === 'play' ? 'rgba(124, 58, 237, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                                            border: `1px solid ${match.playDraw === 'play' ? '#7c3aed' : '#3b82f6'}`
                                        }}>
                                            {getPlayDrawIcon(match.playDraw)}
                                        </Box>

                                        {match.tryhard && (
                                            <Tooltip title="Tryhard">
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '3px',
                                                    background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.25), rgba(234, 88, 12, 0.2))',
                                                    border: '1px solid rgba(249, 115, 22, 0.45)'
                                                }}>
                                                    <LocalFireDepartmentIcon sx={{ color: '#fb923c', fontSize: 12 }} />
                                                </Box>
                                            </Tooltip>
                                        )}

                                        {/* Data - skrócona */}
                                        <Typography variant="caption" sx={{
                                            color: 'rgba(196, 181, 253, 0.6)',
                                            fontSize: '0.6rem',
                                            textAlign: 'center',
                                            lineHeight: 1
                                        }}>
                                            {formatShortDate(match)}
                                        </Typography>

                                        {match.source === 'duels_ink' && (
                                            <Tooltip title="Zaimportowano z duels.ink">
                                                <SyncAltIcon sx={{ fontSize: 12, color: '#a78bfa', opacity: 0.7 }} />
                                            </Tooltip>
                                        )}

                                        {/* Ikona komentarza - tylko jeśli istnieje */}
                                        {match.comment && (
                                            <Box
                                                onClick={() => toggleCommentExpansion(match.id)}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '3px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    backgroundColor: expandedComments.has(match.id)
                                                        ? 'rgba(76, 29, 149, 0.3)'
                                                        : 'rgba(76, 29, 149, 0.1)',
                                                    border: `1px solid ${expandedComments.has(match.id)
                                                        ? 'rgba(76, 29, 149, 0.5)'
                                                        : 'rgba(76, 29, 149, 0.2)'}`,
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(76, 29, 149, 0.2)',
                                                        transform: 'scale(1.1)'
                                                    }
                                                }}
                                            >
                                                <CommentIcon sx={{
                                                    color: expandedComments.has(match.id)
                                                        ? 'rgba(196, 181, 253, 0.9)'
                                                        : 'rgba(196, 181, 253, 0.6)',
                                                    fontSize: 12
                                                }} />
                                            </Box>
                                        )}

                                        {/* Replay link (mobile) */}
                                        {match.replayUrl && (
                                            <Tooltip title="Otwórz powtórkę na duels.ink">
                                                <IconButton
                                                    size="small"
                                                    component="a"
                                                    href={match.replayUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    sx={{ color: '#a78bfa', p: 0.25, '&:hover': { color: '#c4b5fd', backgroundColor: 'rgba(124, 58, 237, 0.2)' } }}
                                                >
                                                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        {/* Przycisk edycji */}
                                        {!deleteMode && onEditMatch && (
                                            <Tooltip title="Edytuj mecz">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); onEditMatch(match); }}
                                                    sx={{
                                                        color: 'rgba(196, 181, 253, 0.7)',
                                                        p: 0.25,
                                                        '&:hover': { color: '#c4b5fd', backgroundColor: 'rgba(76, 29, 149, 0.2)' }
                                                    }}
                                                >
                                                    <EditIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>

                                </Box>
                            ) : (
                                // Desktop/Tablet: Oryginalny layout
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1.5,
                                    backgroundColor: deleteMode && selectedMatches.has(match.id)
                                        ? 'rgba(239, 68, 68, 0.2)'
                                        : 'rgba(17, 24, 39, 0.4)',
                                    border: deleteMode && selectedMatches.has(match.id)
                                        ? '1px solid rgba(239, 68, 68, 0.5)'
                                        : '1px solid rgba(76, 29, 149, 0.2)',
                                    borderRadius: '8px',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: deleteMode && selectedMatches.has(match.id)
                                            ? 'rgba(239, 68, 68, 0.3)'
                                            : 'rgba(17, 24, 39, 0.6)',
                                        border: deleteMode && selectedMatches.has(match.id)
                                            ? '1px solid rgba(239, 68, 68, 0.7)'
                                            : '1px solid rgba(76, 29, 149, 0.4)'
                                    }
                                }}>
                                    {/* Checkbox do zaznaczania (tylko w trybie usuwania) */}
                                    {deleteMode && (
                                        <Checkbox
                                            checked={selectedMatches.has(match.id)}
                                            onChange={() => onSelectMatch(match.id)}
                                            sx={{
                                                color: 'rgba(239, 68, 68, 0.7)',
                                                '&.Mui-checked': {
                                                    color: '#ef4444'
                                                }
                                            }}
                                        />
                                    )}

                                    {/* Lewa strona - informacje o meczu */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                                        {/* Wynik */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: isMobile ? 80 : 100 }}>
                                            {getResultIcon(match.result)}
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: getResultColor(match.result),
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    fontSize: '0.75rem'
                                                }}
                                            >
                                                {match.result === 'win' ? 'Wygrana' : 'Przegrana'}
                                            </Typography>
                                        </Box>

                                        {/* Moja talia */}
                                        {(() => {
                                            const myArch = match.myDeckInfo?.archetype
                                                ? allArchetypes.find(a => a.id === match.myDeckInfo.archetype)
                                                : null;
                                            const myColors = myArch?.ink_colors || [];
                                            return (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: isMobile ? 100 : 120 }}>
                                                    {myColors.length > 0 && (
                                                        <Box sx={{ display: 'flex', gap: 0.2 }}>
                                                            {myColors.map((color, i) => {
                                                                const colorData = inkColors.find(c => c.value.toLowerCase() === (color || '').toLowerCase());
                                                                return (
                                                                    <Avatar key={i} src={colorData?.icon} sx={{ width: 14, height: 14, backgroundColor: getInkColor(color), border: `1px solid ${getInkColor(color)}` }} />
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                                                        {match.myDeckInfo?.name || 'Nieznana talia'}
                                                    </Typography>
                                                </Box>
                                            );
                                        })()}

                                        {/* vs */}
                                        <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.5)', fontSize: '0.75rem' }}>
                                            vs
                                        </Typography>

                                        {/* Przeciwnik - archetyp z kolorami (fallback na opponentColors z DuelSync) */}
                                        {(() => {
                                            const displayColors = match.opponentArchetypeInfo?.ink_colors || match.opponentColors || [];
                                            const oColors = (match.opponentColors || []).map(c => c.toLowerCase());
                                            const matchingArchetypes = !match.opponentArchetypeId && oColors.length > 0
                                                ? allArchetypes.filter(a => {
                                                    const aColors = (a.ink_colors || []).map(c => c.toLowerCase());
                                                    return aColors.length === oColors.length && oColors.every(c => aColors.includes(c));
                                                  })
                                                : [];
                                            return (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: isMobile ? 120 : 150 }}>
                                                    {match.opponentArchetypeId ? (
                                                        <Typography
                                                            variant="body2"
                                                            onClick={() => navigate(`/archetype/${match.opponentArchetypeId}`)}
                                                            sx={{
                                                                color: 'white',
                                                                fontWeight: 500,
                                                                cursor: 'pointer',
                                                                '&:hover': { color: '#c4b5fd', textDecoration: 'underline' }
                                                            }}
                                                        >
                                                            {match.opponentArchetypeInfo?.name}
                                                        </Typography>
                                                    ) : matchingArchetypes.length > 0 ? (
                                                        <Select
                                                            size="small"
                                                            displayEmpty
                                                            value=""
                                                            disabled={!!archetypeAssigning[match.id]}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(e) => { e.stopPropagation(); handleAssignArchetype(match.id, e.target.value); }}
                                                            sx={{
                                                                fontSize: '0.75rem',
                                                                height: 26,
                                                                color: 'rgba(196, 181, 253, 0.6)',
                                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(124, 58, 237, 0.3)' },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(124, 58, 237, 0.6)' },
                                                                '& .MuiSelect-select': { py: 0.25, px: 1 },
                                                            }}
                                                            renderValue={() => archetypeAssigning[match.id] ? '…' : 'Przypisz archetyp'}
                                                        >
                                                            {matchingArchetypes.map(a => (
                                                                <MenuItem key={a.id} value={a.id}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        {(a.ink_colors || []).slice(0, 2).map((color, i) => {
                                                                            const colorData = inkColors.find(c => c.value.toLowerCase() === (color || '').toLowerCase());
                                                                            return <Avatar key={i} src={colorData?.icon} sx={{ width: 14, height: 14, backgroundColor: getInkColor(color), border: `1px solid ${getInkColor(color)}` }} />;
                                                                        })}
                                                                        <Typography variant="body2" sx={{ color: 'white' }}>{a.name}</Typography>
                                                                    </Box>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                                                            {displayColors.length > 0 ? '' : 'Unknown'}
                                                        </Typography>
                                                    )}
                                                    {displayColors.length > 0 && (
                                                        <Box sx={{ display: 'flex', gap: 0.2 }}>
                                                            {displayColors.map((color, colorIndex) => {
                                                                const colorData = inkColors.find(c => c.value.toLowerCase() === (color || '').toLowerCase());
                                                                return (
                                                                    <Avatar
                                                                        key={colorIndex}
                                                                        src={colorData?.icon}
                                                                        sx={{
                                                                            width: 14,
                                                                            height: 14,
                                                                            backgroundColor: getInkColor(color),
                                                                            border: `1px solid ${getInkColor(color)}`
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })()}

                                        {/* Play/Draw */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: isMobile ? 50 : 60 }}>
                                            {getPlayDrawIcon(match.playDraw)}
                                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)', fontSize: '0.7rem' }}>
                                                {match.playDraw === 'play' ? 'Play' : 'Draw'}
                                            </Typography>
                                        </Box>

                                        {match.tryhard && (
                                            <Tooltip title="Tryhard">
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    minWidth: 28,
                                                    height: 24,
                                                    borderRadius: '6px',
                                                    background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.25), rgba(234, 88, 12, 0.2))',
                                                    border: '1px solid rgba(249, 115, 22, 0.45)'
                                                }}>
                                                    <LocalFireDepartmentIcon sx={{ color: '#fb923c', fontSize: 16 }} />
                                                </Box>
                                            </Tooltip>
                                        )}

                                        {/* Data */}
                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)', fontSize: '0.7rem', minWidth: isMobile ? 80 : 100 }}>
                                            {formatDate(match)}
                                        </Typography>

                                        {match.source === 'duels_ink' && (
                                            <Tooltip title="Zaimportowano z duels.ink">
                                                <Chip
                                                    icon={<SyncAltIcon sx={{ fontSize: '12px !important' }} />}
                                                    label="duels.ink"
                                                    size="small"
                                                    sx={{
                                                        height: 18,
                                                        fontSize: '0.6rem',
                                                        backgroundColor: 'rgba(124,58,237,0.15)',
                                                        color: '#a78bfa',
                                                        border: '1px solid rgba(124,58,237,0.25)',
                                                        '& .MuiChip-icon': { color: '#a78bfa' },
                                                    }}
                                                />
                                            </Tooltip>
                                        )}

                                        {/* Komentarz - minimalistycznie w wierszu */}
                                        {match.comment && (
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                minWidth: isMobile ? 100 : 120,
                                                maxWidth: isMobile ? 150 : 200,
                                                overflow: 'hidden'
                                            }}>
                                                <CommentIcon sx={{
                                                    color: 'rgba(196, 181, 253, 0.6)',
                                                    fontSize: 14,
                                                    flexShrink: 0
                                                }} />
                                                <Typography variant="caption" sx={{
                                                    color: 'rgba(196, 181, 253, 0.7)',
                                                    fontSize: '0.7rem',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    flex: 1
                                                }}>
                                                    {match.comment}
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Przycisk edycji */}
                                        {!deleteMode && onEditMatch && (
                                            <Tooltip title="Edytuj mecz">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); onEditMatch(match); }}
                                                    sx={{
                                                        color: 'rgba(196, 181, 253, 0.7)',
                                                        '&:hover': { color: '#c4b5fd', backgroundColor: 'rgba(76, 29, 149, 0.2)' }
                                                    }}
                                                >
                                                    <EditIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}

                                        {/* Replay link (desktop) */}
                                        {match.replayUrl && (
                                            <Tooltip title="Otwórz powtórkę na duels.ink">
                                                <IconButton
                                                    size="small"
                                                    component="a"
                                                    href={match.replayUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    sx={{
                                                        color: '#a78bfa',
                                                        '&:hover': { color: '#c4b5fd', backgroundColor: 'rgba(124, 58, 237, 0.2)' }
                                                    }}
                                                >
                                                    <OpenInNewIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>

                                </Box>
                            )}

                            {/* Rozwinięty komentarz - pod głównym wierszem (tylko mobile) */}
                            {isSmallMobile && match.comment && expandedComments.has(match.id) && (
                                <Box sx={{
                                    mt: 1,
                                    px: 1.5,
                                    py: 1,
                                    backgroundColor: 'rgba(76, 29, 149, 0.1)',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(76, 29, 149, 0.2)',
                                    borderTop: 'none',
                                    borderTopLeftRadius: 0,
                                    borderTopRightRadius: 0
                                }}>
                                    <Typography variant="caption" sx={{
                                        color: 'rgba(196, 181, 253, 0.8)',
                                        fontSize: '0.7rem',
                                        lineHeight: 1.4,
                                        wordBreak: 'break-word',
                                        display: 'block'
                                    }}>
                                        {match.comment}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Fade>
                ))}
            </Box>

            {/* Paginacja */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={(event, page) => setCurrentPage(page)}
                        color="primary"
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: 'rgba(196, 181, 253, 0.7)',
                                '&.Mui-selected': {
                                    backgroundColor: 'rgba(196, 181, 253, 0.2)',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'rgba(196, 181, 253, 0.3)',
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(196, 181, 253, 0.1)',
                                }
                            }
                        }}
                    />
                </Box>
            )}
        </Box>
    );
}

export default MatchesList;
